import {
  UseInfiniteQueryResult,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { CLMInfo, DeliveryModel, DocumentFilters } from "api";
import { useDocumentService } from "contexts/ServiceContext";
import { getProductName } from "util/getProductName";
import { formatQuantityValue, valueOr, valueToDate } from "util/statHelpers";
import { AI_EVENTS, useTrackEvent } from "../../useAppInsight";
import useSelectedCustomerCode from "../../useSelectedCustomerCode";
import useListOfTransportationModes from "../mode-of-transportation/useListOfTransportationModes";

const mapApiDeliveryValues = (
  delivery: DeliveryModel & { HasCLM?: boolean; CLM?: CLMInfo }
): Deliveries => {
  return {
    docEntry: delivery.DocEntry,
    releaseNumber: valueOr(delivery.ReleaseNumber),
    contractNumber: `${valueOr(delivery.ContractNumber)}`,
    shipTo: `${valueOr(delivery.ShipToCode)}`,
    productName: getProductName(delivery.DocumentLines),
    quantity: delivery.CalculatedSumQuantity ?? 0,
    quantityFormatted: formatQuantityValue(delivery.CalculatedSumQuantity), // Ideally we should be consistent and use ContractQuantityCommitted, but that is only available if $expand contains "ContractTotals"
    isReturn: delivery.ObjectType === "oReturns",
    unitOfMeasure: delivery.DocumentLines?.[0]?.UnitOfMeasureCode ?? "",
    createDate: valueToDate(delivery.CreateDateTime),
    docDueDate: valueToDate(delivery.DocDueDate),
    mode: `${valueOr(delivery.ModeOfTransportationCode)}`,
    equipmentId: valueOr(delivery.EquipmentId),
    salesTerms: valueOr(delivery.SalesTermsCode),
    poNumber: valueOr(delivery.NumAtCard),
    clm: delivery.HasCLM
      ? {
          id: delivery.CLM?.ClmId,
          carrierName: valueOr(delivery.CLM?.StatusCarrier),
          date: valueToDate(delivery.CLM?.StatusDateTime),
          details: valueOr(delivery.CLM?.AARDesc),
          currentCity: valueOr(delivery.CLM?.StatusCity),
          currentState: valueOr(delivery.CLM?.StatusState),
          originalEta: valueToDate(delivery.CLM?.OriginalETA ?? undefined),
          updatedEta: valueToDate(delivery.CLM?.ActualETA ?? undefined),
        }
      : undefined,
  };
};

const mapApiDocumentDeliveryFilters = (
  filters?: DocumentsDeliveriesFilters
): DocumentFilters => {
  return {
    CardCodes: filters?.cardCodes,
    CreateDateEnd: filters?.createDateEndDate,
    CreateDateStart: filters?.createDateStartDate,
    Modes: filters?.mode,
    ProductCodes: filters?.product,
    SalesTerms: filters?.salesTerms,
    SearchString: filters?.searchString,
    DocDueDateEnd: filters?.shipDateEndDate,
    DocDueDateStart: filters?.shipDateStartDate,
    ShipToCodes: filters?.shipTo,
  };
};

const useDeliveries = (
  currentSort?: { key: keyof DeliveryModel; direction: string },
  requestBody?: DocumentsDeliveriesFilters
): UseInfiniteQueryResult<{ deliveries: Deliveries[] }> => {
  const cardCodes = useSelectedCustomerCode();

  const documentService = useDocumentService();
  const trackQuery = useTrackEvent(AI_EVENTS.API_REQUEST, {
    cardCodes,
  });
  const trackQueryError = useTrackEvent(AI_EVENTS.API_ERROR, {
    cardCodes,
  });
  const { data: modes, isLoading } = useListOfTransportationModes();

  return useInfiniteQuery({
    queryKey: ["deliveries", { currentSort, requestBody }],
    queryFn: ({ pageParam = 0 }) => {
      const filterParams = {
        sort: currentSort,
        page: pageParam,
      };
      const eventId = trackQuery({
        endpoint: "Delivery/SearchNFilterGridView",
        parameters: filterParams,
      });
      return documentService
        .getDeliveriesList(
          filterParams,
          mapApiDocumentDeliveryFilters(requestBody)
        )
        .then((data) => ({
          ...data,
          value: data.value?.map((value) => ({
            ...value,
            ModeOfTransportationCode:
              modes?.find(
                (mode) => mode.code === value.ModeOfTransportationCode
              )?.name ?? value.ModeOfTransportationCode,
          })),
        }))
        .then(({ value, ...rest }) => ({
          ...rest,
          deliveries: value?.map(mapApiDeliveryValues) ?? [],
        }))
        .catch((err) => {
          trackQueryError(
            {
              statusCode: err.statusCode,
              message: err.message,
            },
            eventId
          );
          throw err;
        });
    },
    enabled: !isLoading,
  });
};

export default useDeliveries;
